class QuantityBreaksOtp {
	constructor(root) {
		this.root = root;
		this.sectionId = root.dataset.section;
		this.productFormId = root.dataset.productFormId;
		this.offerSource = root.dataset.otpSource || 'variants';
		this.hideGalleryVariants = root.dataset.hideGalleryVariants === 'true';
		this.scrollGallery = root.dataset.preventGalleryScroll !== 'true' && !this.hideGalleryVariants;
		this.toggle = root.querySelector('.quantity-breaks-otp-toggle__input');

		this.onChange = this.onChange.bind(this);
		this.onFormSubmit = this.onFormSubmit.bind(this);
		root.addEventListener('change', this.onChange, true);
		this.toggle?.addEventListener('change', this.onChange);
		this.bindFormSubmit();

		this.sync();
	}

	bindFormSubmit() {
		const form = this.getProductForm();
		if (!form || form.dataset.otpBundleSubmit === 'true') return;
		form.dataset.otpBundleSubmit = 'true';
		form.addEventListener('submit', this.onFormSubmit, true);
		form.closest('product-form')?.addEventListener('submit', this.onFormSubmit, true);
	}

	shouldSplitSubscriptionBundle() {
		if (!this.subscriptionEnabled || this.offerSource !== 'quantities') return false;
		const freeQty = parseInt(this.selectedInput?.dataset.freeQty, 10) || 0;
		return freeQty > 0;
	}

	onFormSubmit(event) {
		if (!this.shouldSplitSubscriptionBundle()) return;
		if (this._submittingBundle) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		this.addSubscriptionBundle(event.submitter || event.target?.querySelector('[type="submit"]'));
	}

	get subscriptionEnabled() {
		if (this.root.dataset.otpForceSubscription === 'true') return true;
		return Boolean(this.toggle?.checked);
	}

	get selectedInput() {
		return this.root.querySelector('input[name="quantity"]:checked');
	}

	get selectedBreak() {
		const input = this.selectedInput;
		if (!input) return null;

		if (input.id) {
			const label = this.root.querySelector(`label[for="${input.id}"]`);
			if (label) return label;
		}

		const key = input.dataset.input;
		if (key) {
			return this.root.querySelector(`.quantity-break[data-input="${key}"]`);
		}

		return input.nextElementSibling?.classList?.contains('quantity-break') ? input.nextElementSibling : null;
	}

	onChange() {
		this.sync();
	}

	sync() {
		this.syncForm();
		this.applyPrices();
		this.placeBenefits();
		this.updateBenefits();
		this.updateGallery();
		this.placeAtcSubtext();
		this.updateAtcSubtext();
		this.schedulePdpUpdates();
	}

	getAtcSubtext() {
		return (
			document.querySelector(`[data-otp-atc-subtext][data-section="${this.sectionId}"]`) ||
			this.root.querySelector('[data-otp-atc-subtext]') ||
			document.querySelector(`#product-form-${this.sectionId} [data-otp-atc-subtext]`)
		);
	}

	placeAtcSubtext() {
		const subtext = this.root.querySelector('[data-otp-atc-subtext]');
		if (!subtext) return;

		const form = this.getProductForm();
		const buttonWrap =
			form?.querySelector('.product-form__quantity-and-btn') ||
			document.getElementById(`ProductSubmitButton-${this.sectionId}`)?.closest('.product-form__quantity-and-btn');
		if (!buttonWrap) {
			subtext.classList.add('is-placed');
			return;
		}

		buttonWrap.after(subtext);
		subtext.classList.add('is-placed');
	}

	updateAtcSubtext() {
		const subtext = this.getAtcSubtext();
		const qtyEl = subtext?.querySelector('[data-otp-atc-subtext-qty]');
		if (!qtyEl) return;

		const input = this.selectedInput;
		const quantity = parseInt(input?.dataset.input?.replace('input_', ''), 10) || 1;
		const singular = this.root.dataset.bagSingular || 'Bag';
		const plural = this.root.dataset.bagPlural || 'Bags';
		const unit = quantity > 1 ? plural : singular;
		qtyEl.textContent = unit ? `${quantity} ${unit}` : String(quantity);
	}

	getBenefitsList() {
		return (
			document.querySelector(`[data-otp-benefits][data-section="${this.sectionId}"]`) ||
			this.root.querySelector('[data-otp-benefits]')
		);
	}

	placeBenefits() {
		const list = this.getBenefitsList();
		if (!list) return;

		const form =
			document.getElementById(`ProductForm-${this.sectionId}`) ||
			this.getProductForm()?.closest('product-form') ||
			this.getProductForm();
		if (!form) {
			list.classList.add('is-placed');
			return;
		}

		form.after(list);
		list.classList.add('is-placed');
	}

	updateBenefits() {
		const list = this.getBenefitsList();
		if (!list) return;

		const isSub = this.subscriptionEnabled;
		list.hidden = !isSub;

		const selected = this.selectedBreak;
		if (!selected) return;

		const quantity = selected.dataset.quantity || '1';
		const price = selected.getAttribute(isSub ? 'data-cap-sub-price' : 'data-cap-otp-price') || '';
		const priceEach = selected.getAttribute(isSub ? 'data-cap-sub-each' : 'data-cap-otp-each') || '';
		const comparePrice = isSub
			? selected.getAttribute('data-cap-sub-compare') || selected.getAttribute('data-cap-otp-price') || ''
			: selected.getAttribute('data-cap-otp-compare') || '';

		list.querySelectorAll('[data-otp-benefit]').forEach((el) => {
			if (!el.dataset.text) return;
			el.innerHTML = this.fillTemplate(el.dataset.text, {
				quantity,
				price,
				compare_price: comparePrice,
				amount_saved: '',
				amount_saved_rounded: '',
				price_each: priceEach,
				compare_price_each: comparePrice,
			});
		});
	}

	schedulePdpUpdates() {
		[0, 50, 150, 300].forEach((delay) => {
			setTimeout(() => this.updatePdpPrice(), delay);
		});
	}

	getProductForm() {
		return document.getElementById(this.productFormId);
	}

	syncForm() {
		const form = this.getProductForm();
		const input = this.selectedInput;
		if (!form || !input) return;

		const variantId = input.dataset.variantId;
		const variantInput = form.querySelector('input.product-variant-id, input[name="id"]');
		if (variantInput && variantId) variantInput.value = variantId;

		let planInput = form.querySelector('input[data-quantity-breaks-otp-plan]');
		if (!planInput) {
			planInput = document.createElement('input');
			planInput.type = 'hidden';
			planInput.name = 'selling_plan';
			planInput.setAttribute('data-quantity-breaks-otp-plan', 'true');
			form.appendChild(planInput);
		}

		const sellingPlan = this.subscriptionEnabled ? input.dataset.sellingPlan : '';
		planInput.value = sellingPlan || '';
		planInput.disabled = !sellingPlan;
	}

	getCartUi() {
		return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
	}

	getSectionsToRequest() {
		const cart = this.getCartUi();
		if (cart && typeof cart.getSectionsToRender === 'function') {
			return cart
				.getSectionsToRender()
				.map((section) => section.section || section.id)
				.filter(Boolean);
		}
		return ['cart-drawer', 'cart-icon-bubble'];
	}

	collectGiftItems() {
		const selected = this.selectedBreak;
		if (!selected) return [];

		return [...selected.querySelectorAll('.quantity-break__upsell[data-product]')].filter((el) => {
			if (el.dataset.type === 'gift') return true;
			return el.dataset.toggle === 'true' && el.dataset.selected === 'true';
		});
	}

	async addSubscriptionBundle(submitter) {
		const input = this.selectedInput;
		const form = this.getProductForm();
		if (!input || !form) return;

		const variantId = input.dataset.variantId;
		const sellingPlan = input.dataset.sellingPlan;
		const paidQty = parseInt(input.dataset.paidQty, 10) || parseInt(input.value, 10) || 1;
		const freeQty = parseInt(input.dataset.freeQty, 10) || 0;
		if (!variantId || freeQty < 1) return;

		this._submittingBundle = true;
		const button = submitter?.closest('button') || document.getElementById(`ProductSubmitButton-${this.sectionId}`);
		const spinner = button?.querySelector('.loading-overlay__spinner');
		if (button) button.disabled = true;
		spinner?.classList.remove('hidden');

		try {
			const formData = new FormData();
			formData.append('items[0][id]', variantId);
			formData.append('items[0][quantity]', String(paidQty));
			if (sellingPlan) formData.append('items[0][selling_plan]', sellingPlan);

			formData.append('items[1][id]', variantId);
			formData.append('items[1][quantity]', String(freeQty));

			this.collectGiftItems().forEach((gift, index) => {
				formData.append(`items[${index + 2}][id]`, gift.dataset.product);
				formData.append(`items[${index + 2}][quantity]`, '1');
			});

			const sections = this.getSectionsToRequest();
			if (sections.length) formData.append('sections', sections.join(','));
			formData.append('sections_url', window.location.pathname);

			const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
				method: 'POST',
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				body: formData,
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
			}

			const result = await response.json();
			if (form.querySelector('[data-skip-cart="true"]')) {
				window.location.href = '/checkout';
				return;
			}
			this.updateCartUi(result);
		} catch (error) {
			console.error('Quantity breaks OTP: failed to add subscription bundle', error);
		} finally {
			spinner?.classList.add('hidden');
			if (button) button.disabled = false;
			this._submittingBundle = false;
		}
	}

	updateCartUi(result) {
		const cart = this.getCartUi();
		if (cart && result.sections && typeof cart.renderContents === 'function') {
			cart.renderContents(result);
			if (typeof cart.open === 'function') cart.open();
			cart.classList?.remove('is-empty');
		}

		document.dispatchEvent(
			new CustomEvent('cart:updated', {
				detail: { items: result.items || [] },
			})
		);
	}

	updateGallery() {
		if (this.offerSource === 'quantities' || !this.scrollGallery) return;
		const mediaId = this.selectedInput?.dataset.mediaId;
		if (!mediaId) return;

		const gallery =
			document.querySelector(`media-gallery[data-section="${this.sectionId}"]`) ||
			document.querySelector('media-gallery');
		if (gallery && typeof gallery.setActiveMedia === 'function') {
			gallery.setActiveMedia(mediaId, true);
		}
	}

	applyPrices() {
		const isSub = this.subscriptionEnabled;
		this.root.classList.toggle('subscription-disabled', !isSub);

		this.root.querySelectorAll('.quantity-break').forEach((item) => {
			const isFirst = item.dataset.otpFirst === 'true';
			const price = this.stripMoneyDecimals(isSub ? item.dataset.subPrice : item.dataset.otpPrice);
			const comparePrice = this.stripMoneyDecimals(
				isFirst ? (isSub ? item.dataset.subCompare : item.dataset.otpPrice) : item.dataset.otpCompare
			);
			const amountSaved = isFirst
				? isSub
					? this.stripMoneyDecimals(item.dataset.subSavedFirst)
					: ''
				: this.stripMoneyDecimals(isSub ? item.dataset.subSaved : item.dataset.otpSaved);
			const amountSavedRounded = isFirst
				? isSub
					? this.stripMoneyDecimals(item.dataset.subSavedFirst)
					: ''
				: this.stripMoneyDecimals(isSub ? item.dataset.subSavedRounded : item.dataset.otpSavedRounded);

			const values = {
				quantity: item.dataset.quantity || '',
				price,
				compare_price: comparePrice,
				amount_saved: amountSaved,
				amount_saved_rounded: amountSavedRounded,
				price_each: this.stripMoneyDecimals(isSub ? item.dataset.subPriceEach : item.dataset.otpPriceEach),
				compare_price_each: comparePrice,
			};

			item.querySelectorAll('.variant-price-update[data-text]').forEach((el) => {
				if (el.classList.contains('quantity-break__caption') || el.hasAttribute('data-otp-caption')) return;
				el.innerHTML = this.fillTemplate(el.dataset.text, values);
			});

			const captionEl = item.querySelector('[data-otp-caption], .quantity-break__caption');
			if (captionEl?.dataset.text) {
				const capPrice = item.getAttribute(isSub ? 'data-cap-sub-price' : 'data-cap-otp-price') || '';
				const capCompare = isFirst
					? isSub
						? item.getAttribute('data-cap-sub-compare') || ''
						: capPrice
					: item.getAttribute('data-cap-otp-compare') || '';
				const capSaved = isFirst
					? isSub
						? item.getAttribute('data-cap-sub-saved-first') || ''
						: ''
					: item.getAttribute(isSub ? 'data-cap-sub-saved' : 'data-cap-otp-saved') || '';
				captionEl.innerHTML = this.fillTemplate(captionEl.dataset.text, {
					price: capPrice,
					compare_price: capCompare,
					amount_saved: capSaved,
					amount_saved_rounded: capSaved,
					price_each: item.getAttribute(isSub ? 'data-cap-sub-each' : 'data-cap-otp-each') || '',
					compare_price_each: capCompare,
				});
			}

			const compareEl = item.querySelector('.quantity-break__compare-price');
			if (compareEl) {
				const hide = isFirst ? !isSub : !values.compare_price || values.compare_price === values.price;
				compareEl.classList.toggle('hidden', hide);
			}

			item.querySelectorAll('.quantity-break__badge[data-text]').forEach((el) => {
				if ((el.dataset.text || '').includes('[amount_saved]')) {
					el.classList.toggle('hidden', isFirst && !isSub);
				}
			});
		});

		this.updatePdpPrice();
	}

	getPriceRoots() {
		const roots = new Set();
		document.querySelectorAll(`#price-${this.sectionId} .price, .price-${this.sectionId}, #price-${this.sectionId}`).forEach((el) => {
			if (el.classList.contains('price') || el.querySelector('.main-price, .price__badge-sale, .price-item--sale')) {
				roots.add(el.classList.contains('price') ? el : el.querySelector('.price') || el);
			}
		});
		return [...roots].filter(Boolean);
	}

	updatePdpPrice() {
		const selected = this.selectedBreak;
		if (!selected) return;

		const isSub = this.subscriptionEnabled;
		const isFirst = selected.dataset.otpFirst === 'true';
		const price = this.stripMoneyDecimals(isSub ? selected.dataset.subPrice : selected.dataset.otpPrice);
		const priceCents = parseInt(isSub ? selected.dataset.subPriceCents : selected.dataset.otpPriceCents, 10);
		const compare = isFirst
			? isSub
				? this.stripMoneyDecimals(selected.dataset.subCompare)
				: ''
			: this.stripMoneyDecimals(selected.dataset.otpCompare);
		const saved = isFirst
			? isSub
				? this.stripMoneyDecimals(selected.dataset.subSavedFirst)
				: ''
			: this.stripMoneyDecimals(isSub ? selected.dataset.subSaved : selected.dataset.otpSaved);
		const compareCents = isFirst
			? parseInt(selected.dataset.otpPriceCents, 10)
			: parseInt(selected.dataset.compareCents, 10);
		if (!price) return;

		const onSale =
			Boolean(compare) && Number.isFinite(compareCents) && Number.isFinite(priceCents) && compareCents > priceCents;
		const percent = onSale && compareCents > 0 ? Math.floor(((compareCents - priceCents) * 100) / compareCents) : 0;

		this.getPriceRoots().forEach((root) => {
			root.querySelectorAll('.main-price, .price__sale .price-item--sale, .price__regular .price-item--regular').forEach((el) => {
				if (el.closest('.price__compare-price')) return;
				el.textContent = price;
			});

			root.querySelectorAll('.price__compare-price .price-item--regular, .main-comapre-price').forEach((el) => {
				if (compare) el.textContent = compare;
			});

			root.querySelectorAll('.price__compare-price').forEach((el) => {
				el.classList.toggle('hidden', !onSale);
			});

			root.classList.toggle('price--on-sale', onSale);

			const percentEl = root.querySelector('.badge__percentage-saved');
			const moneyEl = root.querySelector('.badge__money-saved');
			if (percentEl) percentEl.textContent = String(percent);
			if (moneyEl && saved) moneyEl.textContent = saved;

			const badge = root.querySelector('.price__badge-sale');
			if (badge) {
				badge.hidden = !onSale;
				badge.style.display = onSale ? '' : 'none';
			}
		});

		document.querySelectorAll(`.main-atc-price-span-${this.sectionId}`).forEach((el) => {
			el.textContent = price;
		});
	}

	fillTemplate(template, values) {
		return String(template)
			.replaceAll('[compare_price_each]', values.compare_price_each || '')
			.replaceAll('[price_each]', values.price_each || '')
			.replaceAll('[amount_saved_rounded]', values.amount_saved_rounded || '')
			.replaceAll('[compare_price]', values.compare_price || '')
			.replaceAll('[amount_saved]', values.amount_saved || '')
			.replaceAll('[quantity]', values.quantity || '')
			.replaceAll('[price]', values.price || '');
	}

	stripMoneyDecimals(formatted) {
		if (!formatted) return '';
		return String(formatted)
			.replace(/\.\d{2}$/, '')
			.replace(/,\d{2}$/, '')
			.replace(/(\d)[.,]\d{2}(?=\D*$)/, '$1');
	}
}

const initQuantityBreaksOtp = () => {
	document.querySelectorAll('quantity-breaks[data-otp-toggle]').forEach((root) => {
		if (root.dataset.otpReady === 'true') return;
		root.dataset.otpReady = 'true';
		new QuantityBreaksOtp(root);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initQuantityBreaksOtp);
} else {
	initQuantityBreaksOtp();
}
